import { useCallback, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Trash2, Sparkles, Wand2 } from 'lucide-react';
import { dataSourcesApi, DataSource, ProposedSchema, UploadResult, IngestResult, AllocationRunSummary } from '@/lib/dataSources';
import { importMappingsApi, signatureFromPreview, ImportMapping, MatchSignature } from '@/lib/importMappings';
import { api } from '@/lib/api';
import { AppShell } from '@/components/app/AppShell';
import { PromptModal } from '@/components/PromptModal';
import { CurrencyPicker } from '@/components/CurrencyPicker';

interface WorkspaceLite { id: string; name: string; fyLabel: string }

export function UploadPage() {
  const { t } = useTranslation();
  const { wsId } = useParams<{ wsId: string }>();

  const { data: workspace, isLoading: wsLoading } = useQuery({
    queryKey: ['workspace', wsId],
    queryFn: async () => (await api.get<WorkspaceLite>(`/api/workspaces/${wsId}`)).data,
    enabled: !!wsId,
  });

  const { data: sources = [] } = useQuery({
    queryKey: ['data-sources', wsId],
    queryFn: () => dataSourcesApi.list(wsId!),
    enabled: !!wsId,
  });

  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<UploadResult | null>(null);
  const [ingest, setIngest] = useState<IngestResult | null>(null);
  const [allocationsRun, setAllocationsRun] = useState<AllocationRunSummary[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [applying, setApplying] = useState(false);
  // Part 5 — reusable mappings + auto-journaling.
  const [signature, setSignature] = useState<MatchSignature | null>(null);
  const [match, setMatch] = useState<{ mapping: ImportMapping; score: number } | null>(null);
  const [overrideSchema, setOverrideSchema] = useState<ProposedSchema | null>(null);
  const [appliedMappingId, setAppliedMappingId] = useState<string | null>(null);
  const [reviewKey, setReviewKey] = useState(0);
  const [vendor, setVendor] = useState('');

  const { data: vendorPresets = [] } = useQuery({
    queryKey: ['vendor-presets'],
    queryFn: () => dataSourcesApi.vendorPresets(),
    staleTime: Infinity,
  });

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  }, []);

  const submit = useCallback(async () => {
    if (!file || !wsId) return;
    setUploading(true);
    setError(null);
    try {
      const r = await dataSourcesApi.upload(wsId, file, vendor || undefined);
      setResult(r);
      // Build a fingerprint from the preview we already have, then check for a
      // saved mapping that fits — pure server-side heuristic, no extra AI cost.
      const sig = signatureFromPreview(file.name, r.preview.sheets);
      setSignature(sig);
      try {
        const m = await importMappingsApi.match(wsId, sig);
        setMatch(m);
      } catch { /* matching is best-effort — never block the upload */ }
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setError((err as any)?.response?.data?.message || (err as Error).message);
    } finally {
      setUploading(false);
    }
  }, [file, wsId, vendor]);

  const apply = useCallback(async (schema: ProposedSchema, autoRunAllocations: boolean) => {
    if (!result) return;
    setApplying(true);
    try {
      const res = await dataSourcesApi.applySchema(result.dataSource.id, schema, {
        autoRunAllocations,
        importMappingId: appliedMappingId ?? undefined,
      });
      // Surface what landed (counts + any auto-run allocations) + where to go next.
      setIngest(res.ingest);
      setAllocationsRun(res.allocationsRun);
    } catch (err: unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      setError((err as any)?.response?.data?.message || (err as Error).message);
    } finally {
      setApplying(false);
    }
  }, [result, appliedMappingId]);

  const loadMapping = useCallback((m: ImportMapping) => {
    setOverrideSchema(m.schema);
    setAppliedMappingId(m.id);
    setMatch(null);
    setReviewKey((k) => k + 1); // remount SchemaReview with the loaded schema
  }, []);

  const saveMapping = useCallback(async (name: string, schema: ProposedSchema, autoRunAllocations: boolean) => {
    if (!wsId) return;
    await importMappingsApi.create(wsId, { name, schema, matchSignature: signature ?? undefined, autoRunAllocations });
  }, [wsId, signature]);

  if (wsLoading) return <div className="min-h-screen flex items-center justify-center text-ink-500 text-sm">{t('common.loading')}</div>;

  return (
    <AppShell title={t('upload.title')} subtitle={t('upload.subtitle')}>
<main className="mx-auto max-w-3xl px-6 py-12">
        <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">{t('upload.title')}</h1>
        <p className="mt-2 text-ink-600">{t('upload.subtitle')}</p>

        {ingest && (
          <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6">
            <h2 className="text-lg font-semibold text-emerald-800">{t('upload.ingest_done', { defaultValue: 'Data ingested' })}</h2>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: t('upload.ingest_facts', { defaultValue: 'Facts' }), value: ingest.factsInserted.toLocaleString() },
                { label: t('upload.ingest_accounts', { defaultValue: 'Accounts' }), value: String(ingest.accountCodes) },
                { label: t('upload.ingest_periods', { defaultValue: 'Periods' }), value: String(ingest.periodsCovered.length) },
                { label: t('upload.ingest_scenarios', { defaultValue: 'Scenarios' }), value: String(ingest.scenarios.length) },
              ].map((s) => (
                <div key={s.label} className="rounded-lg bg-white border border-emerald-100 px-3 py-2">
                  <div className="num text-xl font-semibold text-ink-900">{s.value}</div>
                  <div className="text-[11px] uppercase tracking-wide text-ink-500">{s.label}</div>
                </div>
              ))}
            </div>
            {allocationsRun && allocationsRun.length > 0 && (
              <div className="mt-4 rounded-lg bg-white border border-emerald-100 p-3">
                <div className="flex items-center gap-1.5 text-[13px] font-semibold text-emerald-800">
                  <Wand2 className="h-3.5 w-3.5" /> {t('upload.allocations_applied', { defaultValue: `Allocations auto-applied: ${allocationsRun.length} rule(s)`, count: allocationsRun.length })}
                </div>
                <ul className="mt-2 space-y-1">
                  {allocationsRun.map((a) => (
                    <li key={a.ruleId} className="flex items-center justify-between text-[12px]">
                      <span className="text-ink-700 truncate">{a.ruleName}</span>
                      {a.status === 'ran'
                        ? <span className="num text-emerald-700">+{a.factsInserted.toLocaleString()}</span>
                        : <span className="text-warn-600" title={a.reason}>{t('upload.alloc_failed', { defaultValue: 'failed' })}</span>}
                    </li>
                  ))}
                </ul>
                <Link to={`/app/workspace/${wsId}/allocations`} className="mt-1.5 inline-block text-[12px] font-semibold text-emerald-700 hover:text-emerald-800">{t('upload.view_allocations', { defaultValue: 'View allocations →' })}</Link>
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-3 text-sm font-semibold">
              <Link to={`/app/workspace/${wsId}`} className="text-emerald-700 hover:text-emerald-800">{t('upload.view_dashboard', { defaultValue: 'View dashboard →' })}</Link>
              <Link to={`/app/workspace/${wsId}/account-search`} className="text-emerald-700 hover:text-emerald-800">{t('upload.view_accounts', { defaultValue: 'Search accounts →' })}</Link>
              <Link to={`/app/workspace/${wsId}/qa`} className="text-emerald-700 hover:text-emerald-800">{t('upload.ask_ai', { defaultValue: 'Ask the AI →' })}</Link>
              <button onClick={() => { setIngest(null); setResult(null); setFile(null); setAllocationsRun(undefined); setMatch(null); setOverrideSchema(null); setAppliedMappingId(null); setSignature(null); }} className="text-ink-600 hover:text-foreground">{t('upload.upload_another', { defaultValue: 'Upload another' })}</button>
            </div>
          </div>
        )}

        {!ingest && !result && (
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="mt-8 rounded-2xl border-2 border-dashed border-ink-300 bg-white p-10 text-center hover:border-accent-500 transition"
          >
            <input
              id="file-input"
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
            <label htmlFor="file-input" className="cursor-pointer">
              <div className="text-ink-700 font-medium">{t('upload.drop')}</div>
              <div className="mt-1 text-xs text-ink-500">{t('upload.limits')}</div>
            </label>
            {vendorPresets.length > 0 && (
              <div className="mt-4 max-w-xs mx-auto text-left" onClick={(e) => e.stopPropagation()}>
                <label className="block text-xs font-semibold text-ink-700 mb-1">{t('upload.vendor_label', { defaultValue: 'Source format (optional)' })}</label>
                <select value={vendor} onChange={(e) => setVendor(e.target.value)} className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm">
                  <option value="">{t('upload.vendor_auto', { defaultValue: 'Auto-detect' })}</option>
                  {vendorPresets.map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
                </select>
                <p className="mt-1 text-[11px] text-ink-400">{t('upload.vendor_hint', { defaultValue: 'For JP-ERP trial-balance or 給与台帳 payroll exports (Shift-JIS handled automatically).' })}</p>
              </div>
            )}
            {file && (
              <div className="mt-4 text-sm text-ink-600">
                {t('upload.selected')} <span className="font-medium text-ink-900">{file.name}</span>
              </div>
            )}
            {/*
             * Hide the action button until a file is actually selected. The
             * permanently-visible-but-disabled button looked like a broken
             * affordance — users couldn't tell why they couldn't click it.
             * With no file, the dropzone label IS the call-to-action; once a
             * file lands, the button appears and takes over.
             */}
            {file && (
              <button
                onClick={submit}
                disabled={uploading}
                className="mt-6 inline-flex items-center px-5 py-2.5 rounded-lg bg-foreground text-background text-sm font-semibold hover:bg-foreground/90 disabled:opacity-60 transition"
              >
                {uploading ? t('upload.analyzing') : t('upload.analyze')}
              </button>
            )}
          </div>
        )}

        {error && (
          <div className="mt-6 rounded-lg bg-warn-500/10 border border-warn-500/30 text-warn-600 px-3 py-2 text-sm">{error}</div>
        )}

        {!ingest && result && match && match.mapping && (
          <div className="mt-6 rounded-xl border border-accent-300 bg-accent-500/5 px-4 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Sparkles className="h-4 w-4 text-accent-600 shrink-0" />
              <span className="text-sm text-ink-700 truncate">
                {t('upload.found_mapping', { defaultValue: `Found a saved mapping "${match.mapping.name}" (${Math.round(match.score * 100)}% match).`, name: match.mapping.name, score: Math.round(match.score * 100) })}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => loadMapping(match.mapping)} className="px-3 py-1.5 rounded-lg bg-accent-500 text-white text-xs font-semibold hover:bg-accent-600">{t('upload.load_mapping', { defaultValue: 'Use it' })}</button>
              <button onClick={() => setMatch(null)} className="text-xs text-ink-500 hover:text-ink-700">{t('common.dismiss', { defaultValue: 'Dismiss' })}</button>
            </div>
          </div>
        )}

        {!ingest && result && (
          <SchemaReview
            key={reviewKey}
            result={result}
            overrideSchema={overrideSchema}
            onApply={apply}
            applying={applying}
            onSaveMapping={saveMapping}
            t={t}
          />
        )}

        {!result && sources.length > 0 && (
          <section className="mt-10">
            <h2 className="text-sm font-semibold text-ink-700">{t('upload.sources_title', { count: sources.length })}</h2>
            <ul className="mt-3 space-y-2">
              {sources.map((s) => <SourceRow key={s.id} source={s} wsId={wsId!} t={t} />)}
            </ul>
          </section>
        )}
      </main>
    </AppShell>
  );
}

function SourceRow({ source, wsId, t }: { source: DataSource; wsId: string; t: (k: string, opts?: Record<string, unknown>) => string }) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const remove = useMutation({
    mutationFn: () => dataSourcesApi.remove(source.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['data-sources', wsId] }),
  });

  const statusTone =
    source.status === 'ready' ? 'text-emerald-700'
    : source.status === 'failed' ? 'text-warn-600'
    : 'text-ink-500';

  return (
    <li className="rounded-xl border border-ink-200 bg-white px-4 py-3 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <div className="text-sm font-medium text-ink-900 truncate">{source.displayName}</div>
        <div className="text-[11px] num">
          <span className="text-ink-500">{source.kind}</span>
          <span className={'ms-2 ' + statusTone}>{source.status}</span>
        </div>
      </div>
      {!confirm ? (
        <button
          onClick={() => setConfirm(true)}
          className="inline-flex items-center gap-1 text-xs text-warn-600 hover:text-warn-700 shrink-0"
        >
          <Trash2 className="h-3.5 w-3.5" /> {t('common.delete')}
        </button>
      ) : (
        <span className="inline-flex items-center gap-2 text-xs shrink-0">
          <span className="text-ink-600">{t('upload.delete_confirm')}</span>
          <button onClick={() => remove.mutate()} disabled={remove.isPending} className="text-warn-700 font-semibold hover:underline">
            {remove.isPending ? t('common.loading') : t('common.yes')}
          </button>
          <button onClick={() => setConfirm(false)} className="text-ink-500 hover:underline">{t('common.no')}</button>
        </span>
      )}
      {remove.isSuccess && remove.data.factsUnlinked > 0 && (
        <span className="text-[11px] text-ink-500">{t('upload.facts_kept', { count: remove.data.factsUnlinked })}</span>
      )}
    </li>
  );
}

function SchemaReview({ result, overrideSchema, onApply, applying, onSaveMapping, t }: {
  result: UploadResult;
  overrideSchema?: ProposedSchema | null;
  onApply: (schema: ProposedSchema, autoRunAllocations: boolean) => void;
  applying: boolean;
  onSaveMapping: (name: string, schema: ProposedSchema, autoRunAllocations: boolean) => Promise<void>;
  t: (k: string, opts?: Record<string, unknown>) => string;
}) {
  const [schema, setSchema] = useState<ProposedSchema | null>(overrideSchema ?? result.schema.proposedSchema);
  const [dimPromptOpen, setDimPromptOpen] = useState(false);
  const [autoRun, setAutoRun] = useState(false);
  const [savePromptOpen, setSavePromptOpen] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const sheet = result.preview.sheets.find((s) => s.name === schema?.sheet_name) || result.preview.sheets[0];

  if (!schema || !sheet) {
    return <div className="mt-8 text-ink-600">{t('upload.could_not_propose')}</div>;
  }

  const headerCells: string[] = sheet.rows[0] ?? [];
  // Part 14: a payroll register binds each amount column to a fixed account and
  // has one sheet-wide period — the inverse of a trial balance's layout.
  const isPayroll = (schema.account_columns?.length ?? 0) > 0;

  function updateField<K extends keyof ProposedSchema>(key: K, value: ProposedSchema[K]) {
    setSchema((s) => (s ? { ...s, [key]: value } : s));
  }
  function updateAccountCol(idx: number, patch: Partial<NonNullable<ProposedSchema['account_columns']>[number]>) {
    setSchema((s) => {
      if (!s?.account_columns) return s;
      const next = s.account_columns.slice();
      next[idx] = { ...next[idx], ...patch };
      return { ...s, account_columns: next };
    });
  }
  function updatePeriodCol(idx: number, patch: Partial<ProposedSchema['period_columns'][number]>) {
    setSchema((s) => {
      if (!s) return s;
      const next = s.period_columns.slice();
      next[idx] = { ...next[idx], ...patch };
      return { ...s, period_columns: next };
    });
  }
  function addPeriodCol() {
    setSchema((s) => s ? { ...s, period_columns: [...s.period_columns, { column: headerCells[0] ?? '', period_start: '', period_end: '', scenario: s.scenario_default }] } : s);
  }
  function removePeriodCol(idx: number) {
    setSchema((s) => s ? { ...s, period_columns: s.period_columns.filter((_, i) => i !== idx) } : s);
  }
  function addDimension() {
    setDimPromptOpen(true);
  }
  function commitNewDimension(name: string) {
    const cleaned = name.trim();
    if (!cleaned) { setDimPromptOpen(false); return; }
    setSchema((s) => s ? { ...s, dimensions: { ...s.dimensions, [cleaned]: headerCells[0] ?? '' } } : s);
    setDimPromptOpen(false);
  }
  function updateDimension(dimName: string, column: string) {
    setSchema((s) => s ? { ...s, dimensions: { ...s.dimensions, [dimName]: column } } : s);
  }
  function removeDimension(dimName: string) {
    setSchema((s) => {
      if (!s) return s;
      const { [dimName]: _gone, ...rest } = s.dimensions;
      void _gone;
      return { ...s, dimensions: rest };
    });
  }

  return (
    <div className="mt-8 space-y-6">
      <div className="rounded-2xl border border-ink-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-ink-900">{t('upload.proposed_schema')}</h2>
            <p className="mt-0.5 text-sm text-ink-600">
              {result.schema.ranHeuristicFallback
                ? t('upload.heuristic_note')
                : t('upload.ai_note', { confidence: (schema.confidence * 100).toFixed(0) })}
            </p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
          <label className="block">
            <span className="block text-xs text-ink-500 mb-1">{t('upload.sheet')}</span>
            <select
              value={schema.sheet_name}
              onChange={(e) => updateField('sheet_name', e.target.value)}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
            >
              {result.preview.sheets.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
            </select>
          </label>

          {!isPayroll && (
            <label className="block">
              <span className="block text-xs text-ink-500 mb-1">{t('upload.account_col')}</span>
              <select
                value={schema.account_code_column}
                onChange={(e) => updateField('account_code_column', e.target.value)}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
              >
                {headerCells.map((h, i) => <option key={`${h}-${i}`} value={h}>{h || `(column ${i + 1})`}</option>)}
              </select>
            </label>
          )}

          <label className="block">
            <span className="block text-xs text-ink-500 mb-1">{t('upload.default_scenario')}</span>
            <select
              value={schema.scenario_default}
              onChange={(e) => updateField('scenario_default', e.target.value)}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
            >
              <option value="actual">actual</option>
              <option value="budget">budget</option>
              <option value="forecast">forecast</option>
              <option value="plan">plan</option>
            </select>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-xs text-ink-500 mb-1">{t('upload.currency_label')}</span>
              <CurrencyPicker
                value={schema.currency_guess ?? ''}
                onChange={(code) => updateField('currency_guess', code || null)}
                ariaLabel={t('upload.currency_label')}
                className="num w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="block text-xs text-ink-500 mb-1">{t('upload.unit_label')}</span>
              <select
                value={schema.unit_guess ?? '1'}
                onChange={(e) => updateField('unit_guess', e.target.value as ProposedSchema['unit_guess'])}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm"
              >
                <option value="1">1</option>
                <option value="1000">1,000 (千 / k)</option>
                <option value="1000000">1,000,000 (百万 / M)</option>
              </select>
            </label>
          </div>
        </div>

        {isPayroll && (
          <div className="mt-5">
            <span className="text-xs text-ink-500">{t('upload.account_cols', { defaultValue: 'Pay components → accounts', count: schema.account_columns!.length })}</span>
            <p className="mt-1 text-[11px] text-ink-400">{t('upload.account_cols_hint', { defaultValue: 'Each amount column lands as an actual on its account, split by the dimensions below.' })}</p>
            <table className="w-full mt-2 text-xs">
              <thead>
                <tr className="text-left text-ink-500 border-b border-ink-100">
                  <th className="py-1 pr-2 font-medium">{t('upload.th_column')}</th>
                  <th className="py-1 pl-2 font-medium">{t('upload.th_account', { defaultValue: 'Account' })}</th>
                </tr>
              </thead>
              <tbody>
                {schema.account_columns!.map((a, i) => (
                  <tr key={i} className="border-b border-ink-50">
                    <td className="py-1.5 pr-2">
                      <select value={a.column} onChange={(e) => updateAccountCol(i, { column: e.target.value })} className="w-full rounded border border-ink-200 px-2 py-1">
                        {headerCells.map((h, j) => <option key={`${h}-${j}`} value={h}>{h || `(col ${j + 1})`}</option>)}
                      </select>
                    </td>
                    <td className="py-1.5 pl-2">
                      <input value={a.account_code} onChange={(e) => updateAccountCol(i, { account_code: e.target.value })} className="num w-full rounded border border-ink-200 px-2 py-1" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-xs text-ink-500 mb-1">{t('upload.period_start_label', { defaultValue: 'Period start' })}</span>
                <input type="date" value={schema.period_start ?? ''} onChange={(e) => updateField('period_start', e.target.value)} className="num w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
              </label>
              <label className="block">
                <span className="block text-xs text-ink-500 mb-1">{t('upload.period_end_label', { defaultValue: 'Period end' })}</span>
                <input type="date" value={schema.period_end ?? ''} onChange={(e) => updateField('period_end', e.target.value)} className="num w-full rounded-lg border border-ink-200 px-3 py-2 text-sm" />
              </label>
            </div>
            {!schema.period_start && <p className="mt-1.5 text-[11px] text-warn-600">{t('upload.payroll_period_hint', { defaultValue: 'Set the pay-month period for this register.' })}</p>}
          </div>
        )}

        {!isPayroll && (
        <div className="mt-5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-ink-500">{t('upload.period_cols', { count: schema.period_columns.length })}</span>
            <button onClick={addPeriodCol} className="text-xs text-accent-600 hover:underline">{t('upload.period_add')}</button>
          </div>
          {schema.period_columns.length === 0 ? (
            <p className="mt-2 text-sm text-warn-600">{t('upload.period_cols_empty')}</p>
          ) : (
            <table className="w-full mt-2 text-xs">
              <thead>
                <tr className="text-left text-ink-500 border-b border-ink-100">
                  <th className="py-1 pr-2 font-medium">{t('upload.th_column')}</th>
                  <th className="py-1 px-2 font-medium">{t('upload.th_period_start')}</th>
                  <th className="py-1 px-2 font-medium">{t('upload.th_period_end')}</th>
                  <th className="py-1 px-2 font-medium">{t('upload.th_scenario')}</th>
                  <th className="py-1 pl-2 font-medium"> </th>
                </tr>
              </thead>
              <tbody>
                {schema.period_columns.map((p, i) => (
                  <tr key={i} className="border-b border-ink-50">
                    <td className="py-1.5 pr-2">
                      <select value={p.column} onChange={(e) => updatePeriodCol(i, { column: e.target.value })} className="w-full rounded border border-ink-200 px-2 py-1">
                        {headerCells.map((h, j) => <option key={`${h}-${j}`} value={h}>{h || `(col ${j + 1})`}</option>)}
                      </select>
                    </td>
                    <td className="py-1.5 px-2">
                      <input type="date" value={p.period_start} onChange={(e) => updatePeriodCol(i, { period_start: e.target.value })} className="num w-full rounded border border-ink-200 px-2 py-1" />
                    </td>
                    <td className="py-1.5 px-2">
                      <input type="date" value={p.period_end} onChange={(e) => updatePeriodCol(i, { period_end: e.target.value })} className="num w-full rounded border border-ink-200 px-2 py-1" />
                    </td>
                    <td className="py-1.5 px-2">
                      <select value={p.scenario} onChange={(e) => updatePeriodCol(i, { scenario: e.target.value })} className="w-full rounded border border-ink-200 px-2 py-1">
                        <option value="actual">actual</option>
                        <option value="budget">budget</option>
                        <option value="forecast">forecast</option>
                      </select>
                    </td>
                    <td className="py-1.5 pl-2">
                      <button onClick={() => removePeriodCol(i)} className="text-ink-400 hover:text-warn-600">×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        )}

        <div className="mt-5">
          <div className="flex items-center justify-between">
            <span className="text-xs text-ink-500">{t('upload.dimensions', { count: Object.keys(schema.dimensions).length })}</span>
            <button onClick={addDimension} className="text-xs text-accent-600 hover:underline">{t('upload.dim_add')}</button>
          </div>
          {Object.keys(schema.dimensions).length === 0 ? (
            <p className="mt-2 text-sm text-ink-500">{t('upload.dimensions_empty')}</p>
          ) : (
            <ul className="mt-2 space-y-1">
              {Object.entries(schema.dimensions).map(([dimName, col]) => (
                <li key={dimName} className="flex items-center gap-2 text-xs">
                  <span className="num text-ink-700 w-24">{dimName}</span>
                  <span className="text-ink-400">→</span>
                  <select value={col} onChange={(e) => updateDimension(dimName, e.target.value)} className="flex-1 rounded border border-ink-200 px-2 py-1">
                    {headerCells.map((h, j) => <option key={`${h}-${j}`} value={h}>{h || `(col ${j + 1})`}</option>)}
                  </select>
                  <button onClick={() => removeDimension(dimName)} className="text-ink-400 hover:text-warn-600">×</button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <label className="mt-5 flex items-center gap-2 text-sm text-ink-700">
          <input type="checkbox" checked={autoRun} onChange={(e) => setAutoRun(e.target.checked)} className="rounded border-ink-300" />
          {t('upload.auto_allocate', { defaultValue: 'Auto-run cost allocations after import' })}
          <span className="text-[11px] text-ink-400">{t('upload.auto_allocate_hint', { defaultValue: '(Growth+; re-applies matching rules for the imported periods)' })}</span>
        </label>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            onClick={() => onApply(schema, autoRun)}
            disabled={applying || (isPayroll ? !schema.period_start || !schema.period_end : schema.period_columns.length === 0)}
            className="inline-flex items-center px-5 py-2.5 rounded-lg bg-accent-500 text-white text-sm font-semibold hover:bg-accent-600 disabled:opacity-60 transition"
          >
            {applying ? t('upload.ingesting') : t('upload.apply')}
          </button>
          <button
            onClick={() => { setSaveErr(null); setSavePromptOpen(true); }}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg border border-ink-200 text-ink-700 text-sm font-semibold hover:bg-foreground/[0.04] transition"
          >
            <Wand2 className="h-4 w-4" /> {saved ? t('upload.mapping_saved', { defaultValue: 'Saved ✓' }) : t('upload.save_mapping', { defaultValue: 'Save as mapping' })}
          </button>
          {saveErr && <span className="text-xs text-warn-600">{saveErr}</span>}
        </div>
      </div>

      <div className="rounded-2xl border border-ink-200 bg-white p-6">
        <h3 className="text-sm font-semibold text-ink-700">
          {t('upload.preview_title', { sheet: sheet.name, rows: sheet.rowCount, cols: sheet.columnCount })}
        </h3>
        <div className="mt-3 overflow-x-auto">
          <table className="text-xs">
            <tbody>
              {sheet.rows.slice(0, 12).map((row, i) => (
                <tr key={i} className={i === 0 ? 'bg-ink-50' : ''}>
                  {row.map((c, j) => (
                    <td key={j} className="num border-b border-ink-100 px-2 py-1 text-ink-700 whitespace-nowrap max-w-[180px] overflow-hidden text-ellipsis">{c}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <PromptModal
        open={dimPromptOpen}
        title={t('upload.dim_prompt')}
        placeholder={t('upload.dim_example', { defaultValue: 'dept / product / region' })}
        onConfirm={commitNewDimension}
        onCancel={() => setDimPromptOpen(false)}
      />

      <PromptModal
        open={savePromptOpen}
        title={t('upload.mapping_name_prompt', { defaultValue: 'Name this mapping' })}
        placeholder={t('upload.mapping_name_ph', { defaultValue: 'Monthly actuals (freee)' })}
        onConfirm={async (name) => {
          setSavePromptOpen(false);
          if (!name.trim() || !schema) return;
          try { await onSaveMapping(name.trim(), schema, autoRun); setSaved(true); }
          catch (e) { setSaveErr(((e as { response?: { data?: { message?: string } } })?.response?.data?.message) ?? (e as Error).message); }
        }}
        onCancel={() => setSavePromptOpen(false)}
      />
    </div>
  );
}
